import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { useListPropertiesQuery } from "../../../store/api/propertiesApi";
import { useListUsersQuery } from "../../../store/api/usersApi";

interface EditLeaseModalProps {
	isOpen: boolean;
	onClose: () => void;
	onUpdateLease: (leaseId: string, leaseData: any) => Promise<void>;
	lease: any | null; // Transformed lease from parent
	isLoading?: boolean;
}

const EditLeaseModal: React.FC<EditLeaseModalProps> = ({
	isOpen,
	onClose,
	onUpdateLease,
	lease,
	isLoading = false,
}) => {
	const [formData, setFormData] = useState({
		propertyId: "",
		tenantId: "",
		startDate: "",
		endDate: "",
		rentAmount: "",
		securityDeposit: "",
		status: "draft",
		terms: "",
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	// Fetch properties and tenants
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 1000 });
	const { data: usersData } = useListUsersQuery({
		page_size: 1000,
		role: "tenant",
	});

	// Populate form when lease changes
	useEffect(() => {
		if (lease && isOpen) {
			// Use IDs directly from lease object if available
			// The lease object from parent should have property.id and tenant.id
			// If not, fall back to searching by name/email
			let propertyId = lease.property?.id || "";
			let tenantId = lease.tenant?.id || "";

			// If IDs not available, try to find them from fetched data
			if (!propertyId && lease.property?.name) {
				const property = propertiesData?.results?.find(
					(p) => p.name === lease.property?.name || p.address === lease.property?.unit
				);
				propertyId = property?.id || "";
			}

			if (!tenantId && lease.tenant?.email) {
				const tenant = usersData?.results?.find(
					(u) => u.email === lease.tenant?.email ||
						`${u.first_name} ${u.last_name}`.trim() === lease.tenant?.name
				);
				tenantId = tenant?.id || "";
			}

			setFormData({
				propertyId,
				tenantId,
				startDate: lease.startDate
					? new Date(lease.startDate).toISOString().split("T")[0]
					: "",
				endDate: lease.endDate
					? new Date(lease.endDate).toISOString().split("T")[0]
					: "",
				rentAmount: lease.rentAmount?.toString() || "",
				securityDeposit: lease.securityDeposit?.toString() || "",
				status: lease.status || "draft",
				terms: lease.terms || "",
			});
		}
	}, [lease, isOpen, propertiesData, usersData]);

	const propertyOptions =
		propertiesData?.results?.map((p) => ({
			value: p.id,
			label: `${p.name} - ${p.address}`,
		})) || [];

	const tenantOptions =
		usersData?.results?.map((u) => ({
			value: u.id,
			label: `${u.first_name} ${u.last_name} (${u.email})`,
		})) || [];

	const statusOptions = [
		{ value: "draft", label: "Draft" },
		{ value: "active", label: "Active" },
		{ value: "expired", label: "Expired" },
		{ value: "terminated", label: "Terminated" },
		{ value: "renewed", label: "Renewed" },
	];

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		if (!formData.propertyId) newErrors.propertyId = "Property is required";
		if (!formData.tenantId) newErrors.tenantId = "Tenant is required";
		if (!formData.startDate) newErrors.startDate = "Start date is required";
		if (!formData.endDate) newErrors.endDate = "End date is required";
		if (!formData.rentAmount) newErrors.rentAmount = "Rent amount is required";

		if (formData.startDate && formData.endDate) {
			const start = new Date(formData.startDate);
			const end = new Date(formData.endDate);
			if (end <= start) {
				newErrors.endDate = "End date must be after start date";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm() || !lease) return;

		try {
			await onUpdateLease(lease.id, {
				property: formData.propertyId,
				tenant: formData.tenantId,
				start_date: formData.startDate,
				end_date: formData.endDate,
				monthly_rent: Number(formData.rentAmount),
				security_deposit: formData.securityDeposit
					? Number(formData.securityDeposit)
					: undefined,
				status: formData.status as any,
				terms: formData.terms,
			});
			onClose();
		} catch (error) {
			console.error("Error updating lease:", error);
		}
	};

	if (!isOpen || !lease) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Edit Lease Agreement
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Lease Code: {lease.leaseCode || "N/A"} • ID: {lease.id}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
							disabled={isLoading}
						/>
					</div>
				</div>

				{/* Content */}
				<form onSubmit={handleSubmit}>
					<div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
						{/* Lease Code Display (Read-only) */}
						{lease.leaseCode && (
							<div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
								<div className="flex items-center space-x-2">
									<Icon name="FileText" size={16} className="text-primary" />
									<div>
										<p className="text-xs text-muted-foreground">Lease Code</p>
										<p className="text-sm font-mono font-medium text-foreground">
											{lease.leaseCode}
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											This code is auto-generated and cannot be changed
										</p>
									</div>
								</div>
							</div>
						)}

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Select
								label="Property"
								options={propertyOptions}
								value={formData.propertyId}
								onChange={(e) =>
									handleInputChange("propertyId", String(e.target.value))
								}
								error={errors.propertyId}
								required
								searchable
							/>

							<Select
								label="Tenant"
								options={tenantOptions}
								value={formData.tenantId}
								onChange={(e) =>
									handleInputChange("tenantId", String(e.target.value))
								}
								error={errors.tenantId}
								required
								searchable
							/>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Start Date"
								type="date"
								value={formData.startDate}
								onChange={(e) => handleInputChange("startDate", e.target.value)}
								error={errors.startDate}
								required
							/>

							<Input
								label="End Date"
								type="date"
								value={formData.endDate}
								onChange={(e) => handleInputChange("endDate", e.target.value)}
								error={errors.endDate}
								required
							/>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Input
								label="Monthly Rent"
								type="number"
								placeholder="0"
								value={formData.rentAmount}
								onChange={(e) =>
									handleInputChange("rentAmount", e.target.value)
								}
								error={errors.rentAmount}
								required
							/>

							<Input
								label="Security Deposit"
								type="number"
								placeholder="0"
								value={formData.securityDeposit}
								onChange={(e) =>
									handleInputChange("securityDeposit", e.target.value)
								}
							/>
						</div>

						<Select
							label="Status"
							options={statusOptions}
							value={formData.status}
							onChange={(e) =>
								handleInputChange("status", String(e.target.value))
							}
						/>

						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Terms & Conditions
							</label>
							<textarea
								value={formData.terms}
								onChange={(e) => handleInputChange("terms", e.target.value)}
								rows={4}
								className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
								placeholder="Enter lease terms and conditions..."
							/>
						</div>
					</div>

					{/* Footer */}
					<div className="p-6 border-t border-border">
						<div className="flex items-center justify-end space-x-3">
							<Button
								type="button"
								variant="ghost"
								onClick={onClose}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={isLoading}
								iconName="Check"
								iconPosition="left"
							>
								{isLoading ? "Updating..." : "Update Lease"}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditLeaseModal;
